import React, { useEffect, useLayoutEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Popover } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { useWindowResize } from '@app/helpers';

import { TooltipContent, type TooltipContentProps } from './DashboardLegacyTooltip';

export function useLegacyTooltip() {
    const { t } = useTranslation(['dashboard_cards']);
    const { currentEmoji, currentFree, emojis } = useAppSelector((state) => state.userEmojis);
    const primaryYat = useAppSelector((state) => state.user.primaryYat);
    const [position, setPosition] = useState<TooltipContentProps['position']>('bottom-start');
    const [shouldUseLegacyTooltip, setShouldUseLegacyTooltip] = useState<boolean>(false);
    const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
    const windowSize = useWindowResize();
    const navigate = useNavigate();

    useEffect(() => {
        if ((emojis.length > 1 || (emojis.length === 1 && currentFree)) && currentEmoji === primaryYat) {
            setShouldUseLegacyTooltip(true);
        } else {
            setShouldUseLegacyTooltip(false);
        }
    }, [emojis.length, currentFree, currentEmoji, primaryYat]);

    useEffect(() => {
        if (!windowSize.width) return;
        setPosition(windowSize.width < 1700 ? 'bottom-start' : 'left-start');
    }, [windowSize.width]);

    function displayLegacyTooltip() {
        if (!shouldUseLegacyTooltip) return;
        setTooltipVisible(true);
    }

    useLayoutEffect(() => {
        setTooltipVisible(shouldUseLegacyTooltip);
    }, [shouldUseLegacyTooltip]);

    function goToProfile() {
        navigate(`/${primaryYat ?? 'profile'}`);
    }

    const LegacyTooltipWrapper = ({ children }: { children?: React.ReactElement }) => {
        return (
            <Popover
                trigger={<span onClick={() => setTooltipVisible(true)}>{children}</span>}
                controlled
                placement={position}
                open={tooltipVisible}
                action={() => goToProfile()}
                actionContent={t('legacy_edit_accept')}
                variant="wrapper"
            >
                <TooltipContent position={position} />
            </Popover>
        );
    };

    return {
        showLegacyTooltip: shouldUseLegacyTooltip,
        displayLegacyTooltip,
        LegacyTooltipWrapper,
    };
}
